import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn, stripDiacritics } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CustomReducedDto } from "@/types/api/api-types";

interface CustomsComboboxProps {
  value?: string;
  onValueChange?: (value: string | null) => void;
  customs: CustomReducedDto[];
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  isDisabled?: boolean;
}

const CustomFlag = ({ flagCode }: { flagCode: string }) => {
  const [imageError, setImageError] = React.useState(false);

  if (imageError) {
    return (
      <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-gray-200">
        <span className="text-xs text-[#94A3B8]">{flagCode}</span>
      </div>
    );
  }

  return (
    <img
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${flagCode.toUpperCase()}.svg`}
      alt=""
      className="h-4 w-4 shrink-0 rounded-sm object-cover"
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
};

export const CustomsCombobox = ({
  value,
  onValueChange,
  customs,
  // named Pays because asked by Cata but concerns customs, not countries
  placeholder,
  className,
  clearable = false,
  isDisabled = false,
}: CustomsComboboxProps) => {
  const { t } = useTranslation("shared");
  const [open, setOpen] = React.useState(false);
  const commandGroupRef = React.useRef<HTMLDivElement>(null);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  const selected = customs.find((c) => c.id === value);

  const sorted = React.useMemo(
    () =>
      [...customs].sort((a, b) => {
        if (a.isArchived && !b.isArchived) return 1;
        if (!a.isArchived && b.isArchived) return -1;
        const aIsUsa = a.label.toLowerCase().startsWith("usa");
        const bIsUsa = b.label.toLowerCase().startsWith("usa");
        if (aIsUsa && !bIsUsa) return -1;
        if (!aIsUsa && bIsUsa) return 1;
        return a.label.localeCompare(b.label, "fr");
      }),
    [customs],
  );

  React.useEffect(() => {
    if (!open) return;

    let cleanup: (() => void) | null = null;
    let retryCount = 0;

    const findInput = (): HTMLInputElement | null => {
      if (!popoverContentRef.current) return null;
      return (
        popoverContentRef.current.querySelector<HTMLInputElement>("input[cmdk-input]") ||
        popoverContentRef.current.querySelector<HTMLInputElement>("input")
      );
    };

    const handleInput = () => {
      if (commandGroupRef.current) commandGroupRef.current.scrollTop = 0;
    };

    const setupListener = () => {
      const input = findInput();
      if (!input) {
        if (retryCount++ < 10) requestAnimationFrame(setupListener);
        return;
      }
      input.addEventListener("input", handleInput);
      cleanup = () => input.removeEventListener("input", handleInput);
    };

    requestAnimationFrame(setupListener);
    return () => cleanup?.();
  }, [open]);

  return (
    <div
      className={cn(
        "relative flex h-[2.4rem] w-full items-center rounded-lg border bg-white px-4 transition-all duration-200",
        {
          "border-blue-600": open,
          "border-gray-300": !open,
          "pointer-events-none bg-gray-50": isDisabled,
          "hover:border-gray-400": !isDisabled && !open,
        },
        className,
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="flex h-full w-full justify-between border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus:ring-0 focus:outline-none"
            disabled={isDisabled}
          >
            <div className="flex min-w-0 items-center gap-2">
              {selected ? (
                <>
                  {selected.flagCode && <CustomFlag flagCode={selected.flagCode} />}
                  <span className="truncate">
                    {selected.label}
                    {selected.isArchived && (
                      <span className="ml-1 text-xs text-gray-400">
                        {t("customsCombobox.archived")}
                      </span>
                    )}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground font-normal">
                  {placeholder ?? t("customsCombobox.placeholder")}
                </span>
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent ref={popoverContentRef} className="w-[320px] p-0">
          <Command
            filter={(itemValue, search) => {
              const normalized = stripDiacritics(search.toLowerCase());
              return stripDiacritics(itemValue.toLowerCase()).includes(normalized) ? 1 : 0;
            }}
          >
            <CommandInput placeholder={t("customsCombobox.searchPlaceholder")} className="h-9" />
            <CommandList>
              <CommandEmpty>{t("customsCombobox.empty")}</CommandEmpty>
              <CommandGroup ref={commandGroupRef} className="max-h-[300px] overflow-y-auto">
                {sorted.map((custom) => (
                  <CommandItem
                    key={custom.id}
                    value={
                      custom.isArchived
                        ? t("customsCombobox.archivedSearchValue", { label: custom.label })
                        : custom.label
                    }
                    onSelect={() => {
                      onValueChange?.(custom.id === value ? null : custom.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {custom.flagCode && <CustomFlag flagCode={custom.flagCode} />}
                      <span>
                        {custom.label}
                        {custom.isArchived && (
                          <span className="ml-1 text-xs text-gray-400">
                            {t("customsCombobox.archived")}
                          </span>
                        )}
                      </span>
                    </div>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        value === custom.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {clearable && value && !isDisabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-1/2 right-8 h-6 w-6 -translate-y-1/2 p-0 hover:bg-transparent"
          onClick={() => onValueChange?.(null)}
        >
          <X className="h-3 w-3" />
          <span className="sr-only">{t("customsCombobox.clear")}</span>
        </Button>
      )}
    </div>
  );
};
